import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { api } from '../lib/api';
import {
  getLockState,
  saveLockState,
  clearLockState,
  updateLastActivity as updateActivityTimestamp,
} from '../lib/security';
import type { UserSecuritySettings } from '../types/security';

interface AppLockContextType {
  isLocked: boolean;
  isEnabled: boolean;
  hasPin: boolean;
  settings: UserSecuritySettings | null;
  lockApp: () => void;
  unlockApp: () => void;
  forceLogout: () => void;
  refreshSettings: () => Promise<void>;
  initializeLock: () => Promise<void>;
}

const AppLockContext = createContext<AppLockContextType | undefined>(undefined);

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
const ACTIVITY_THROTTLE = 2000; // Update activity every 2 seconds max

export function AppLockProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { user, userData, signOut } = useAuth();
  const [isLocked, setIsLocked] = useState(false);
  const [settings, setSettings] = useState<UserSecuritySettings | null>(null);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const activityThrottleRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const visibilityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const previousUserIdRef = useRef<string | null>(null);

  // Check if app lock feature is available for current user role
  const isLockAvailable = userData?.role === 'clinic_admin' || userData?.role === 'doctor';

  console.log('🔐 AppLockContext - user:', user?.email, 'role:', userData?.role, 'isLockAvailable:', isLockAvailable);

  /**
   * Fetch security settings from backend
   */
  const refreshSettings = useCallback(async () => {
    console.log('🔐 refreshSettings called - isLockAvailable:', isLockAvailable, 'user:', user?.email);
    // Don't make API calls if no user is logged in or lock is not available
    if (!user || !isLockAvailable) {
      console.warn('🔐 refreshSettings aborted - no user or lock not available for this role');
      return;
    }

    try {
      const response = await api.security.getSettings();
      if (response.data) {
        console.log('🔐 Security settings loaded:', {
          hasPin: response.data.hasPin,
          lockEnabled: response.data.lockEnabled,
          autoLockTimeout: response.data.autoLockTimeout,
        });
        setSettings(response.data);
        // Update local storage
        saveLockState({
          isEnabled: response.data.lockEnabled,
          autoLockTimeout: response.data.autoLockTimeout,
          hasPin: response.data.hasPin,
        });
      }
    } catch (error) {
      console.error('Failed to fetch security settings:', error);
    }
  }, [isLockAvailable, user]);

  /**
   * Initialize lock on mount
   */
  const initializeLock = useCallback(async () => {
    // Don't initialize if no user or lock is not available
    if (!user || !isLockAvailable) {
      console.log('🔐 initializeLock skipped - no user or lock not available');
      return;
    }

    const currentUserId = userData?.id?.toString() || user?.email || '';
    const isFreshLogin = previousUserIdRef.current !== currentUserId;
    
    // Update the previous user ID ref
    previousUserIdRef.current = currentUserId;

    await refreshSettings();

    // If this is a fresh login (user just logged in), update activity and don't check for lock
    if (isFreshLogin) {
      updateActivityTimestamp();
      lastActivityRef.current = Date.now();
      console.log('✅ Fresh login detected - activity timestamp updated, skipping lock check');
      return;
    }

    // This is a page refresh - check if should be locked BEFORE updating activity timestamp
    // This ensures page refresh doesn't bypass the lock
    const state = getLockState();
    if (state.isEnabled && state.hasPin) {
      const timeSinceActivity = (Date.now() - state.lastActivity) / 1000;
      if (timeSinceActivity >= state.autoLockTimeout) {
        console.log('🔒 App should be locked - time since activity:', Math.floor(timeSinceActivity), 'seconds');
        setIsLocked(true);
        // DON'T update activity timestamp - keep the lock
        return;
      }
    }

    // Only update activity timestamp if NOT being locked
    updateActivityTimestamp();
    lastActivityRef.current = Date.now();
    console.log('✅ App initialized - activity timestamp updated');
  }, [user, userData?.id, isLockAvailable, refreshSettings]);

  /**
   * Lock the app
   */
  const lockApp = useCallback(() => {
    const state = getLockState();
    if (state.isEnabled && state.hasPin) {
      setIsLocked(true);
      console.log('🔒 App locked');
    }
  }, []);

  /**
   * Unlock the app
   */
  const unlockApp = useCallback(() => {
    setIsLocked(false);
    // Reset activity timestamp after unlock
    const now = Date.now();
    updateActivityTimestamp();
    lastActivityRef.current = now;
    console.log('🔓 App unlocked - activity timer reset');
  }, []);

  /**
   * Force logout (after max failed attempts)
   */
  const forceLogout = useCallback(() => {
    clearLockState();
    signOut();
    navigate('/login');
    console.log('🚪 Force logout triggered');
  }, [signOut, navigate]);

  /**
   * Handle user activity (throttled)
   */
  const handleActivity = useCallback(() => {
    if (isLocked || !isLockAvailable) {
      return;
    }

    const now = Date.now();
    lastActivityRef.current = now;

    // Throttle activity updates
    if (activityThrottleRef.current) {
      return;
    }

    activityThrottleRef.current = setTimeout(() => {
      updateActivityTimestamp();
      activityThrottleRef.current = null;
    }, ACTIVITY_THROTTLE);
  }, [isLocked, isLockAvailable]);

  /**
   * Check for inactivity and auto-lock
   */
  const checkInactivity = useCallback(() => {
    if (!isLockAvailable || isLocked) {
      return;
    }

    const state = getLockState();
    if (!state.isEnabled || !state.hasPin) {
      console.log('⏱️ Auto-lock disabled or no PIN configured');
      return;
    }

    const now = Date.now();
    const inactiveTime = (now - lastActivityRef.current) / 1000; // seconds

    console.log(`⏱️ Inactivity check: ${Math.floor(inactiveTime)}s / ${state.autoLockTimeout}s`);

    if (inactiveTime >= state.autoLockTimeout) {
      console.log('🔒 Auto-lock triggered due to inactivity');
      lockApp();
    }
  }, [isLockAvailable, isLocked, lockApp]);

  /**
   * Handle page visibility change (lock when background)
   */
  const handleVisibilityChange = useCallback(() => {
    if (!isLockAvailable) {
      return;
    }

    if (document.hidden) {
      // App went to background - DON'T lock immediately
      // The regular inactivity check will handle locking after timeout
      console.log('📱 App went to background - inactivity timer will handle locking');
    } else {
      // App came to foreground
      console.log('📱 App came to foreground');

      // Check if should lock due to inactivity while in background
      const state = getLockState();
      if (state.isEnabled && state.hasPin) {
        const now = Date.now();
        const timeSinceLastActivity = (now - lastActivityRef.current) / 1000;

        console.log(`⏱️ Time since last activity: ${Math.floor(timeSinceLastActivity)}s, timeout: ${state.autoLockTimeout}s`);

        if (timeSinceLastActivity >= state.autoLockTimeout) {
          console.log('🔒 Locking due to inactivity while in background');
          setIsLocked(true);
        } else {
          console.log(`⏱️ Not locking - only ${Math.floor(timeSinceLastActivity)}s inactive (need ${state.autoLockTimeout}s)`);
        }
      }
    }
  }, [isLockAvailable]);

  /**
   * Setup activity listeners and inactivity timer
   */
  useEffect(() => {
    if (!isLockAvailable || !settings?.lockEnabled) {
      return;
    }

    console.log(`🔐 Auto-lock enabled with ${settings.autoLockTimeout}s timeout`);

    // Add activity listeners
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Setup inactivity check timer (check every 10 seconds)
    inactivityTimerRef.current = setInterval(checkInactivity, 10000);

    // Cleanup
    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if (inactivityTimerRef.current) {
        clearInterval(inactivityTimerRef.current);
      }
      if (activityThrottleRef.current) {
        clearTimeout(activityThrottleRef.current);
      }
      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current);
      }
    };
  }, [isLockAvailable, settings?.lockEnabled, handleActivity, handleVisibilityChange, checkInactivity]);

  /**
   * Initialize on mount and user change
   */
  useEffect(() => {
    if (userData && isLockAvailable) {
      initializeLock();
    } else {
      // Clear lock state if user not eligible
      setSettings(null);
      setIsLocked(false);
    }
  }, [userData, isLockAvailable, initializeLock]);

  /**
   * Cleanup on logout
   */
  useEffect(() => {
    if (!userData) {
      clearLockState();
      setSettings(null);
      setIsLocked(false);
      previousUserIdRef.current = null; // Reset previous user tracking
    }
  }, [userData]);

  const value: AppLockContextType = {
    isLocked,
    isEnabled: settings?.lockEnabled ?? false,
    hasPin: settings?.hasPin ?? false,
    settings,
    lockApp,
    unlockApp,
    forceLogout,
    refreshSettings,
    initializeLock,
  };

  return <AppLockContext.Provider value={value}>{children}</AppLockContext.Provider>;
}

export function useAppLock() {
  const context = useContext(AppLockContext);
  if (context === undefined) {
    throw new Error('useAppLock must be used within AppLockProvider');
  }
  return context;
}
